import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Child, Memory, Tag, AppSettings } from '@/types';

interface AppState {
  children: Child[];
  memories: Memory[];
  tags: Tag[];
  settings: AppSettings;
  currentChildId: string | null;
  
  // 孩子管理
  addChild: (child: Omit<Child, 'id'>) => void;
  updateChild: (id: string, child: Partial<Child>) => void;
  deleteChild: (id: string) => void;
  setCurrentChild: (id: string | null) => void;
  
  // 记忆管理
  addMemory: (memory: Omit<Memory, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateMemory: (id: string, memory: Partial<Memory>) => void;
  deleteMemory: (id: string) => void;
  toggleFavorite: (id: string) => void;
  
  // 标签管理
  addTag: (tag: Omit<Tag, 'id'>) => void;
  deleteTag: (id: string) => void;
  
  // 设置
  updateSettings: (settings: Partial<AppSettings>) => void;
  
  // 数据导入导出
  exportData: () => string;
  importData: (json: string) => boolean;
  clearAll: () => void;
}

const defaultSettings: AppSettings = {
  theme: 'light',
  language: 'zh-CN',
  autoBackup: true,
  backupFrequency: 'weekly',
  cloudSync: false,
};

const defaultChildren: Child[] = [
  {
    id: 'dababy',
    name: '大宝',
    type: 'dababy',
    birthDate: '2020-01-01',
    color: '#FFB6B9',
  },
  {
    id: 'secondbaby',
    name: '二宝',
    type: 'secondbaby',
    birthDate: '2022-06-01',
    color: '#A8D8EA',
  },
  {
    id: 'family',
    name: '全家',
    type: 'family',
    birthDate: '',
    color: '#FFEAA7',
  },
];

const defaultTags: Tag[] = [
  { id: '1', name: '童言趣语', color: '#FFB6B9' },
  { id: '2', name: '第一次', color: '#A8D8EA' },
  { id: '3', name: '成长', color: '#B5EAD7' },
  { id: '4', name: '旅行', color: '#FFEAA7' },
  { id: '5', name: '节日', color: '#E0BBE4' },
];

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      children: defaultChildren,
      memories: [],
      tags: defaultTags,
      settings: defaultSettings,
      currentChildId: null,
      
      addChild: (child) => {
        const newChild: Child = {
          ...child,
          id: Date.now().toString(),
        };
        set((state) => ({ children: [...state.children, newChild] }));
      },
      
      updateChild: (id, child) => {
        set((state) => ({
          children: state.children.map((c) =>
            c.id === id ? { ...c, ...child } : c
          ),
        }));
      },
      
      deleteChild: (id) => {
        set((state) => ({
          children: state.children.filter((c) => c.id !== id),
          memories: state.memories.filter((m) => m.childId !== id),
        }));
      },
      
      setCurrentChild: (id) => set({ currentChildId: id }),
      
      addMemory: (memory) => {
        const now = new Date().toISOString();
        const newMemory: Memory = {
          ...memory,
          id: Date.now().toString(),
          createdAt: now,
          updatedAt: now,
        };
        set((state) => ({ memories: [newMemory, ...state.memories] }));
      },
      
      updateMemory: (id, memory) => {
        set((state) => ({
          memories: state.memories.map((m) =>
            m.id === id
              ? { ...m, ...memory, updatedAt: new Date().toISOString() }
              : m
          ),
        }));
      },
      
      deleteMemory: (id) => {
        set((state) => ({
          memories: state.memories.filter((m) => m.id !== id),
        }));
      },
      
      toggleFavorite: (id) => {
        set((state) => ({
          memories: state.memories.map((m) =>
            m.id === id ? { ...m, isFavorite: !m.isFavorite } : m
          ),
        }));
      },
      
      addTag: (tag) => {
        const newTag: Tag = {
          ...tag,
          id: Date.now().toString(),
        };
        set((state) => ({ tags: [...state.tags, newTag] }));
      },
      
      deleteTag: (id) => {
        set((state) => ({
          tags: state.tags.filter((t) => t.id !== id),
          memories: state.memories.map((m) => ({
            ...m,
            tags: m.tags.filter((t) => t !== id),
          })),
        }));
      },
      
      updateSettings: (settings) => {
        set((state) => ({
          settings: { ...state.settings, ...settings },
        }));
      },
      
      exportData: () => {
        const { children, memories, tags, settings } = get();
        return JSON.stringify({ children, memories, tags, settings }, null, 2);
      },
      
      importData: (json) => {
        try {
          const data = JSON.parse(json);
          if (data.children && data.memories && data.tags) {
            set({
              children: data.children,
              memories: data.memories,
              tags: data.tags,
              settings: { ...defaultSettings, ...data.settings },
            });
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },
      
      clearAll: () => {
        set({
          children: defaultChildren,
          memories: [],
          tags: defaultTags,
          currentChildId: null,
        });
      },
    }),
    {
      name: 'family-moments-storage',
      version: 1,
    }
  )
);
